// src/app/modules/Dashboard/pages/SecurityDashboard.jsx
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import * as actions from "../_redux/dashboard/Actions";
import { useOrgConfig } from "../../../../hooks/useOrgConfig";
import { ActivityThisWeek } from '../components/ActivityThisWeek';
import './security-dashboard.scss';
import '../components/activity.scss'; // or append to security-dashboard.scss

import Moment from 'moment';
import { DashboardLoader } from '../components/DashboardLoader';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import * as functions from '../../../../globals/functions';
import { useFeatureGate } from '../components/FeatureGate';

import {
  Shield, ShieldAlert, AlertTriangle, Users, Building2,
  TrendingUp, Activity, RefreshCw, ArrowRight, Clock, Eye, Lock,
} from 'lucide-react';

export function SecurityDashboard() {
  const dispatch = useDispatch();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { getLabel, hasFeature, org } = useOrgConfig();
  const { canUseFeature, guardAction } = useFeatureGate();

  const {
    analytics, threatAlerts, safetyScore,
    unifiedDashboard, capacityStatus, operationsAnalytics, listLoading,
  } = useSelector((state) => ({
    analytics:           state.dashboard.security_analytics,
    threatAlerts:        state.dashboard.threat_alerts,
    safetyScore:         state.dashboard.safetyScore,
    unifiedDashboard:    state.dashboard.unifiedDashboard,
    capacityStatus:      state.dashboard.capacityStatus,
    operationsAnalytics: state.dashboard.operationsAnalytics,
    listLoading:         state.dashboard.listLoading,
  }), shallowEqual);

  useEffect(() => {
    if (org?.organization_id) loadDashboardData();
  }, [org?.organization_id]);

  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(() => { if (org?.organization_id) loadDashboardData(); }, 30000);
    return () => clearInterval(iv);
  }, [autoRefresh, org?.organization_id]);

  function loadDashboardData() {
    if (!org?.organization_id) return;
    let lat = 6.5244, lon = 3.3792;
    if (org?.address?.coordinates) [lon, lat] = org.address.coordinates;

    dispatch(actions.fetchAllData('security_analytics',   org.organization_id));
    dispatch(actions.fetchAllData('threat_alerts',        org.organization_id, { page: 1, perPage: 3, status: 'active' }));
    dispatch(actions.fetchAllData('safety_score',         null, { lat, lng: lon, radius: 5 }));
    dispatch(actions.fetchAllData('unified_dashboard',    org.organization_id));
    dispatch(actions.fetchAllData('capacity_status',      org.organization_id));
    dispatch(actions.fetchAllData('operations_analytics', org.organization_id, { period: 'week' }));
  }

  const hasData = analytics || unifiedDashboard;

  const todayActivity =
    (unifiedDashboard?.statistics?.visitors?.today_total  || 0) +
    (unifiedDashboard?.statistics?.staff?.today_total     || 0) +
    (unifiedDashboard?.statistics?.residents?.today_total || 0);

  const kpis = [
    {
      icon: <Shield size={20} />,
      bg: '#F0EEFF', color: '#5341C4',
      label: 'Security Score',
      value: analytics?.security_score?.overall_score || safetyScore?.score || 0,
      unit: '/100',
      footer: safetyScore?.level
        ? <span className={`sd-pill sd-pill--${safetyScore.level}`}>{safetyScore.level.replace('_', ' ')}</span>
        : `Top ${analytics?.security_score?.comparison_to_network || 15}% in network`,
    },
    {
      icon: <Building2 size={20} />,
      bg: '#EFF6FF', color: '#2563EB',
      label: getLabel('currentlyInside'),
      value: capacityStatus?.total || 0,
      footer: `${capacityStatus?.visitors || 0} visitors`,
    },
    {
      icon: <ShieldAlert size={20} />,
      bg: '#FEF2F2', color: '#DC2626',
      label: 'Active Threats',
      value: threatAlerts?.length || 0,
      footer: `${analytics?.threat_intelligence?.high_risk_visitors_blocked || 0} prevented this month`,
    },
    {
      icon: <TrendingUp size={20} />,
      bg: '#F0FDF4', color: '#16a34a',
      label: "Today's Activity",
      value: todayActivity,
      footer: `${unifiedDashboard?.statistics?.visitors?.currently_active || 0} currently active`,
    },
  ];

  return (
    <div className="security-dashboard">

      {/* ── Header ── */}
      <div className="sd-header">
        <div className="sd-header__left">
          <h1>{getLabel('dashboard')}</h1>
          <p>{org?.name} · Real-time overview</p>
        </div>
        <div className="sd-header__actions">
          <button className="sd-btn" onClick={loadDashboardData} disabled={listLoading}>
            <RefreshCw size={14} className={listLoading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <SubscriptionBanner />

      {listLoading && !hasData && <DashboardLoader />}

      {hasData && (
        <>
          {/* ── KPI Grid ── */}
          <div className="sd-kpi-grid">
            {kpis.map((k, i) => (
              <div key={i} className="sd-kpi">
                <div className="sd-kpi__icon" style={{ background: k.bg, color: k.color }}>
                  {k.icon}
                </div>
                <div className="sd-kpi__body">
                  <span className="sd-kpi__label">{k.label}</span>
                  <div className="sd-kpi__value">
                    {k.value}
                    {k.unit && <span className="unit">{k.unit}</span>}
                  </div>
                  <div className="sd-kpi__footer">{k.footer}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Threat Alerts ── */}
          {threatAlerts?.length > 0 && (
            <div className="sd-card">
              <div className="sd-card__header sd-threat-header">
                <div className="sd-card__title">
                  <ShieldAlert size={16} style={{ color: '#DC2626' }} />
                  Active Threat Alerts
                  <span className="sd-threat-badge">{threatAlerts.length}</span>
                </div>
                <a href="/threat-alerts" className="sd-card__link">
                  View All <ArrowRight size={13} />
                </a>
              </div>
              <div className="sd-card__body">
                <div className="sd-alerts-grid">
                  {threatAlerts.slice(0, 3).map((alert) => (
                    <div key={alert.alert_id} className="sd-alert-item">
                      <div className="sd-alert-item__top">
                        <span className={`sd-severity sd-severity--${alert.severity}`}>
                          {alert.severity}
                        </span>
                        <span className="sd-alert-item__time">
                          <Clock size={11} />
                          {Moment(alert.created_at).fromNow()}
                        </span>
                      </div>
                      <div className="sd-alert-item__text">
                        {functions.formatAlertTitle(alert.description)}
                      </div>
                      {alert.visitor_name && (
                        <div className="sd-alert-item__visitor">
                          <Users size={11} />{alert.visitor_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Main content grid ── */}
          <div className="sd-grid">

            {/* ── Activity This Week (new component) ── */}
            <ActivityThisWeek
              analytics={analytics}
              unifiedDashboard={unifiedDashboard}
              operationsAnalytics={operationsAnalytics}
              capacityStatus={capacityStatus}
            />

            {/* ── Right sidebar ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Quick stats */}
              <div className="sd-card">
                <div className="sd-card__header">
                  <div className="sd-card__title">Quick Stats</div>
                </div>
                <div className="sd-card__body" style={{ padding: 0 }}>
                  <div className="sd-quick-stats">
                    {[
                      { icon: <Eye size={15} />,        bg: '#F0EEFF', color: '#5341C4', label: 'Currently Active', value: capacityStatus?.total || 0 },
                      { icon: <TrendingUp size={15} />, bg: '#F0FDF4', color: '#16a34a', label: "Today's Total",     value: todayActivity },
                      { icon: <Activity size={15} />,   bg: '#EFF6FF', color: '#2563EB', label: 'This Week',         value: operationsAnalytics?.total_visitors || unifiedDashboard?.overall_stats?.total_visitors || 0 },
                      ...(operationsAnalytics?.average_duration_minutes > 0 ? [{
                        icon: <Clock size={15} />, bg: '#FFFBEB', color: '#D97706',
                        label: 'Avg Duration',
                        value: `${Math.round(operationsAnalytics.average_duration_minutes)}m`,
                      }] : []),
                    ].map((s, i) => (
                      <div key={i} className="sd-quick-stat">
                        <div className="sd-quick-stat__icon" style={{ background: s.bg, color: s.color }}>
                          {s.icon}
                        </div>
                        <div>
                          <div className="sd-quick-stat__label">{s.label}</div>
                          <div className="sd-quick-stat__value">{s.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* AI Safety Intelligence — gated */}
              {canUseFeature('ml_risk_scoring') ? null : (
                <div className="sd-card sd-card--locked">
                  <div className="sd-card__header">
                    <div className="sd-card__title">
                      <Shield size={14} style={{ color: '#DC2626' }} />
                      AI Safety Intelligence
                    </div>
                  </div>
                  <div className="sd-card__body sd-locked-body">
                    <div className="sd-lock-icon">
                      <Lock size={22} />
                    </div>
                    <p className="sd-lock-desc">
                      AI-powered visitor risk scoring and behavioural threat detection.
                    </p>
                    <button
                      className="sd-lock-btn"
                      onClick={() => guardAction(() => {}, 'ml_risk_scoring')}
                    >
                      Upgrade to unlock <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              )}

              {/* Quick links */}
              <div className="sd-card">
                <div className="sd-card__header">
                  <div className="sd-card__title">Quick Access</div>
                </div>
                <div className="sd-card__body">
                  <div className="sd-quick-actions">
                    <a href="/access-control" className="sd-action-link sd-action-link--primary">
                      <Building2 size={15} />{getLabel('accessControl')}
                    </a>
                    <a href="/visitors" className="sd-action-link sd-action-link--secondary">
                      <Users size={15} />{getLabel('visitors')}
                    </a>
                    {hasFeature('hasStaff') && (
                      <a href="/members" className="sd-action-link sd-action-link--secondary">
                        <Shield size={15} />{getLabel('staff')}
                      </a>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </>
      )}
    </div>
  );
}